import React, { useCallback, useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SaveIcon from "@mui/icons-material/Save";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import Item from "../components/Item";
import AdResourceService from "./AdResourceService";
import { ADMIN_ADVERTISEMENT_PATH, advertisementResourceDetailPath } from "./advertisementRoutes";
import { downloadFileNameForAdResource } from "./adDownloadFileName";
import {
    getAdGenerateResourceType,
    getAdResourcePreviewUrl,
    getAdResourceVideoUrl,
    isAppIconResourceType,
    isVideoAdResource,
} from "./adResourceTypes.config";
import { fetchUrlAsBase64, readFileAsBase64 } from "./adReferenceImageUtils";
import { downloadJigsawIconPack } from "./jigsawIconPack";

function formatDimensions(width, height) {
    const w = Number(width);
    const h = Number(height);
    if (Number.isFinite(w) && w > 0 && Number.isFinite(h) && h > 0) {
        return `${w}x${h}`;
    }
    return "—";
}

function formatSizeMb(sizeBytes) {
    const bytes = Number(sizeBytes);
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return "—";
    }
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
}

function formatFileFormat(contentType, s3Key, imageUrl, videoUrl) {
    const mime = typeof contentType === "string" ? contentType.trim() : "";
    if (mime) {
        const subtype = mime.includes("/") ? mime.split("/").pop() : mime;
        const pretty = String(subtype || "")
            .replace(/^x-/, "")
            .toUpperCase();
        if (pretty) {
            return `${pretty} (${mime})`;
        }
        return mime;
    }
    const path = String(s3Key || imageUrl || videoUrl || "").split("?")[0];
    const match = path.match(/\.([a-z0-9]+)$/i);
    if (match?.[1]) {
        return match[1].toUpperCase();
    }
    return "—";
}

function resourceToForm(data) {
    if (!data) return null;
    const storyIdRaw = data.storyId ?? data.story_id ?? "";
    let storyId = typeof storyIdRaw === "string" ? storyIdRaw.trim() : "";
    if (!storyId) {
        const s3Key = data.s3Key ?? data.s3_key ?? "";
        const match = String(s3Key).match(/story\/video\/([^/]+)\.mp4/i);
        if (match?.[1]) storyId = match[1];
    }
    return {
        resourceType: data.resourceType ?? "",
        prompt: data.prompt ?? "",
        storyId,
        width: data.width != null ? String(data.width) : "",
        height: data.height != null ? String(data.height) : "",
        sizeBytes: data.sizeBytes != null ? String(data.sizeBytes) : "",
    };
}

export default function AdResourceDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPack, setDownloadingPack] = useState(false);
    const [imageRefreshKey, setImageRefreshKey] = useState(0);
    const [resource, setResource] = useState(null);
    const [form, setForm] = useState(null);
    const [error, setError] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [openUrlDialog, setOpenUrlDialog] = useState(false);
    const [referenceImageBase64, setReferenceImageBase64] = useState("");
    const [referenceImagePreview, setReferenceImagePreview] = useState(null);
    const [strength, setStrength] = useState("0.35");
    const [loadingReferenceFromUrl, setLoadingReferenceFromUrl] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const load = useCallback(async () => {
        if (!id) {
            setError("Missing ad resource id");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        const result = await AdResourceService.getById(id);
        setLoading(false);
        if (!result.success) {
            setError(result.error || "Failed to load");
            setResource(null);
            setForm(null);
            return;
        }
        setResource(result.data);
        setForm(resourceToForm(result.data));
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const setField = (key) => (event) => {
        setForm((prev) => (prev ? { ...prev, [key]: event.target.value } : prev));
    };

    const handleSave = async () => {
        if (!id || !form) return;
        const body = {};
        if (form.resourceType.trim()) body.resourceType = form.resourceType.trim();
        body.prompt = form.prompt.trim();
        const storyIdText = (form.storyId ?? "").trim();
        if (storyIdText) body.storyId = storyIdText;
        const s3Key = resource?.s3Key ?? resource?.s3_key ?? "";
        const imageUrlValue = resource?.imageUrl ?? resource?.image_url ?? "";
        const videoUrlValue = resource?.videoUrl ?? resource?.video_url ?? "";
        if (s3Key) body.s3Key = s3Key;
        if (imageUrlValue) body.imageUrl = imageUrlValue;
        if (videoUrlValue) body.videoUrl = videoUrlValue;
        const width = Number(resource?.width ?? form.width);
        const height = Number(resource?.height ?? form.height);
        if (Number.isFinite(width) && width > 0) body.width = width;
        if (Number.isFinite(height) && height > 0) body.height = height;
        const contentType = resource?.contentType ?? resource?.content_type;
        if (contentType) body.contentType = contentType;
        const sizeBytes = Number(resource?.sizeBytes ?? form.sizeBytes);
        body.sizeBytes = Number.isFinite(sizeBytes) && sizeBytes > 0 ? sizeBytes : null;

        setSaving(true);
        const result = await AdResourceService.update(id, body);
        setSaving(false);
        if (!result.success) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Save failed",
            });
            return;
        }
        setResource(result.data);
        setForm(resourceToForm(result.data));
        setSnackbar({ open: true, severity: "success", message: "Ad resource updated" });
    };

    const handleConfirmDelete = async (targetId) => {
        const result = await AdResourceService.delete(targetId);
        if (!result.success) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Delete failed",
            });
            return;
        }
        navigate(ADMIN_ADVERTISEMENT_PATH);
    };

    const handleClearReferenceImage = () => {
        setReferenceImageBase64("");
        setReferenceImagePreview(null);
    };

    const handleReferenceImageChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            handleClearReferenceImage();
            return;
        }
        try {
            const base64 = await readFileAsBase64(file);
            setReferenceImageBase64(base64);
            setReferenceImagePreview(URL.createObjectURL(file));
        } catch (readError) {
            handleClearReferenceImage();
            setSnackbar({
                open: true,
                severity: "error",
                message: readError?.message || "Failed to read reference image",
            });
        }
    };

    const handleUseCurrentImageAsReference = async () => {
        if (!rawPreviewUrl || isVideo) return;
        setLoadingReferenceFromUrl(true);
        try {
            const base64 = await fetchUrlAsBase64(rawPreviewUrl);
            setReferenceImageBase64(base64);
            setReferenceImagePreview(previewUrl);
            setSnackbar({ open: true, severity: "success", message: "Current image set as reference" });
        } catch (loadError) {
            setSnackbar({
                open: true,
                severity: "error",
                message: loadError?.message || "Failed to load current image",
            });
        } finally {
            setLoadingReferenceFromUrl(false);
        }
    };

    const handleRegenerate = async () => {
        if (!form) return;
        const promptText = form.prompt.trim();
        const resourceType = form.resourceType.trim();
        const storyIdText = (form.storyId ?? "").trim();
        const isStoryAnimation = resourceType === "STORY_ANIMATION";
        if (!resourceType) {
            setSnackbar({ open: true, severity: "error", message: "Resource type is required" });
            return;
        }
        if (isStoryAnimation) {
            if (!storyIdText) {
                setSnackbar({ open: true, severity: "error", message: "Story ID is required for STORY_ANIMATION" });
                return;
            }
        } else if (!promptText) {
            setSnackbar({ open: true, severity: "error", message: "Enter a prompt before re-generating" });
            return;
        }

        setRegenerating(true);
        const result = await AdResourceService.generate({
            resourceType,
            prompt: promptText,
            storyId: storyIdText || undefined,
            referenceImage: referenceImageBase64 || undefined,
            strength: referenceImageBase64 ? strength : undefined,
        });
        setRegenerating(false);

        if (!result.success) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Re-generate failed",
            });
            return;
        }

        const newId = result.data?.id;
        if (newId) {
            navigate(advertisementResourceDetailPath(newId));
            return;
        }

        setResource(result.data);
        setForm(resourceToForm(result.data));
        setImageRefreshKey(Date.now());
        setSnackbar({
            open: true,
            severity: "success",
            message: isStoryAnimation ? "Video re-generated" : "Image re-generated",
        });
    };

    const rawPreviewUrl = getAdResourcePreviewUrl(resource);
    const rawVideoUrl = getAdResourceVideoUrl(resource);
    const s3Key = resource?.s3Key ?? resource?.s3_key ?? "";
    const isAppIcon = isAppIconResourceType(form?.resourceType);
    const isVideo = isVideoAdResource(resource);
    const typeConfig = getAdGenerateResourceType(form?.resourceType);
    const isStoryAnimation = form?.resourceType === "STORY_ANIMATION" || typeConfig?.storyIdRequired;
    const canRegenerate = !isVideo || isStoryAnimation;
    const canSubmitRegenerate =
        canRegenerate &&
        form?.resourceType?.trim() &&
        (isStoryAnimation ? (form.storyId ?? "").trim() : form.prompt.trim());
    const previewUrl = rawPreviewUrl
        ? `${rawPreviewUrl}${rawPreviewUrl.includes("?") ? "&" : "?"}v=${imageRefreshKey}`
        : null;
    const videoUrl = rawVideoUrl
        ? `${rawVideoUrl}${rawVideoUrl.includes("?") ? "&" : "?"}v=${imageRefreshKey}`
        : null;
    const mediaUrl = isVideo ? videoUrl : previewUrl;
    const previewWidth = Number(resource?.width ?? form?.width);
    const previewHeight = Number(resource?.height ?? form?.height);
    const previewAspectRatio =
        Number.isFinite(previewWidth) && previewWidth > 0 && Number.isFinite(previewHeight) && previewHeight > 0
            ? `${previewWidth} / ${previewHeight}`
            : "1 / 1";

    const handleDownloadMedia = async () => {
        const downloadUrl = isVideo ? rawVideoUrl : rawPreviewUrl;
        if (!downloadUrl) return;
        const fileName = downloadFileNameForAdResource(resource, isVideo);

        setDownloading(true);
        try {
            const response = await fetch(downloadUrl);
            if (!response.ok) {
                throw new Error("Download failed");
            }
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (downloadError) {
            setSnackbar({
                open: true,
                severity: "error",
                message: downloadError?.message || "Download failed",
            });
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadIconPack = async () => {
        if (!rawPreviewUrl) return;
        setDownloadingPack(true);
        try {
            const zipFileName =
                form?.resourceType === "WORD_WHEEL_APP_ICON"
                    ? "word-wheel-quest-app-icon-pack.zip"
                    : form?.resourceType === "SLIDING_APP_ICON"
                      ? "sliding-app-icon-pack.zip"
                      : "jigsaw-app-icon-pack.zip";
            await downloadJigsawIconPack(rawPreviewUrl, zipFileName);
            setSnackbar({ open: true, severity: "success", message: "Icon pack downloaded" });
        } catch (packError) {
            setSnackbar({
                open: true,
                severity: "error",
                message: packError?.message || "Icon pack download failed",
            });
        } finally {
            setDownloadingPack(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: "100%", overflow: "hidden" }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(ADMIN_ADVERTISEMENT_PATH)}
                sx={{ mb: 2 }}
            >
                Back to resources
            </Button>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : null}

            {!loading && error ? (
                <Typography color="error" sx={{ py: 2 }}>
                    {error}
                </Typography>
            ) : null}

            {!loading && !error && form ? (
                <Item sx={{ textAlign: "left", p: { xs: 2, md: 3 }, overflow: "hidden" }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ mb: 3, flexWrap: "wrap", rowGap: 1 }}
                    >
                        <Box>
                            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                                Ad resource
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", fontFamily: "monospace", mt: 0.5, wordBreak: "break-all" }}
                            >
                                {resource?.id}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                                sx={{ textTransform: "none" }}
                            >
                                Save
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => setDeleteOpen(true)}
                                sx={{ textTransform: "none" }}
                            >
                                Delete
                            </Button>
                        </Stack>
                    </Stack>

                    <Grid container spacing={3} alignItems="stretch" sx={{ width: "100%", m: 0 }}>
                        <Grid size={{ xs: 12, sm: isVideo ? 12 : 4, md: isVideo ? 12 : 3 }} sx={{ minWidth: 0 }}>
                            {(isVideo ? rawPreviewUrl || rawVideoUrl : previewUrl) ? (
                                <Stack spacing={1.5} sx={{ height: "100%", maxWidth: isVideo ? 720 : 300 }}>
                                    {isVideo ? (
                                        <>
                                            {previewUrl ? (
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
                                                    >
                                                        Preview image
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            width: "100%",
                                                            maxWidth: 720,
                                                            aspectRatio: previewAspectRatio,
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            borderRadius: 1,
                                                            bgcolor: "action.hover",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            overflow: "hidden",
                                                        }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src={previewUrl}
                                                            alt=""
                                                            referrerPolicy="no-referrer"
                                                            sx={{
                                                                display: "block",
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "contain",
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            ) : null}
                                            {rawVideoUrl ? (
                                                <TextField
                                                    label="Video URL"
                                                    value={rawVideoUrl}
                                                    fullWidth
                                                    size="small"
                                                    multiline
                                                    minRows={2}
                                                    slotProps={{ input: { readOnly: true } }}
                                                />
                                            ) : null}
                                            {videoUrl ? (
                                                <Box
                                                    component="video"
                                                    key={imageRefreshKey}
                                                    src={videoUrl}
                                                    controls
                                                    playsInline
                                                    sx={{
                                                        display: "block",
                                                        width: "100%",
                                                        maxWidth: 720,
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        borderRadius: 1,
                                                        bgcolor: "grey.900",
                                                    }}
                                                />
                                            ) : null}
                                        </>
                                    ) : (
                                        <Box
                                            sx={{
                                                width: "100%",
                                                maxWidth: 300,
                                                aspectRatio: previewAspectRatio,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 1,
                                                bgcolor: "action.hover",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={mediaUrl}
                                                alt=""
                                                referrerPolicy="no-referrer"
                                                sx={{
                                                    display: "block",
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            startIcon={<OpenInNewIcon />}
                                            onClick={() => setOpenUrlDialog(true)}
                                            disabled={isVideo ? !rawVideoUrl : !rawPreviewUrl}
                                            sx={{ textTransform: "none" }}
                                        >
                                            {isVideo ? "Open video" : "Open"}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            startIcon={
                                                downloading ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : (
                                                    <DownloadIcon />
                                                )
                                            }
                                            onClick={handleDownloadMedia}
                                            disabled={downloading || (isVideo ? !rawVideoUrl : !rawPreviewUrl)}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Download
                                        </Button>
                                    </Stack>
                                    {isAppIcon ? (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            fullWidth
                                            startIcon={
                                                downloadingPack ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : (
                                                    <FolderZipIcon />
                                                )
                                            }
                                            onClick={handleDownloadIconPack}
                                            disabled={downloadingPack || !rawPreviewUrl}
                                            sx={{ textTransform: "none", maxWidth: 300 }}
                                        >
                                            Download icon pack (ZIP)
                                        </Button>
                                    ) : null}
                                </Stack>
                            ) : (
                                <Typography color="text.secondary">{isVideo ? "No video" : "No image"}</Typography>
                            )}
                        </Grid>

                        <Grid size={{ xs: 12, sm: 8, md: 9 }} sx={{ minWidth: 0 }}>
                            <Stack spacing={2} sx={{ height: "100%" }}>
                                <TextField
                                    label="Resource type"
                                    value={form.resourceType}
                                    onChange={setField("resourceType")}
                                    fullWidth
                                    size="small"
                                />

                                <Paper variant="outlined" sx={{ p: 2, textAlign: "left", flex: 1 }}>
                                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                        File info
                                    </Typography>
                                    <Stack spacing={0.75} sx={{ mt: 1 }}>
                                        <Typography variant="body2">
                                            <Box component="span" color="text.secondary" sx={{ mr: 1 }}>
                                                Format
                                            </Box>
                                            {formatFileFormat(
                                                resource?.contentType ?? resource?.content_type,
                                                resource?.s3Key ?? resource?.s3_key,
                                                resource?.imageUrl ?? resource?.image_url,
                                                resource?.videoUrl ?? resource?.video_url,
                                            )}
                                        </Typography>
                                        <Typography variant="body2">
                                            <Box component="span" color="text.secondary" sx={{ mr: 1 }}>
                                                Dimensions
                                            </Box>
                                            {formatDimensions(resource?.width ?? form.width, resource?.height ?? form.height)}
                                        </Typography>
                                        <Typography variant="body2">
                                            <Box component="span" color="text.secondary" sx={{ mr: 1 }}>
                                                Size
                                            </Box>
                                            {formatSizeMb(resource?.sizeBytes ?? form.sizeBytes)}
                                        </Typography>
                                        {isAppIcon ? (
                                            <Box sx={{ mt: 1 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    Icon pack (ZIP)
                                                </Typography>
                                                <Typography component="div" variant="caption" color="text.secondary">
                                                    app-icon-1024.png, favicon.ico (16 & 32), logo192.png, logo512.png,
                                                    apple-touch-icon.png
                                                </Typography>
                                            </Box>
                                        ) : null}
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Grid>

                        <Grid size={12}>
                            <Divider sx={{ mb: 0.5 }} />
                            <Stack spacing={2.5} sx={{ pt: 1.5 }}>
                                {typeConfig?.storyIdRequired ? (
                                    <TextField
                                        label="Story ID"
                                        value={form.storyId ?? ""}
                                        onChange={setField("storyId")}
                                        fullWidth
                                        size="small"
                                        required
                                        helperText="Story UUID — Luma Ray2 animates that story's image. Video is saved under story/{storyId}/video/ in S3."
                                    />
                                ) : null}
                                <TextField
                                    label="Prompt"
                                    value={form.prompt}
                                    onChange={setField("prompt")}
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={typeConfig?.storyIdRequired ? 2 : 4}
                                />
                                <Alert severity="info" sx={{ textAlign: "left" }}>
                                    {isStoryAnimation
                                        ? "Edit the story ID and optional motion prompt, then re-generate to create a new video."
                                        : isVideo
                                          ? "Edit the motion prompt and re-generate from Generate Media if you want a new video."
                                          : "Add comments to the prompt and re-generate if you don't like this image."}
                                    <Typography component="span" variant="body2" sx={{ display: "block", mt: 1 }}>
                                        Model:{" "}
                                        <code>
                                            {isVideo
                                                ? "amazon.nova-reel-v1:1"
                                                : "stability.stable-image-ultra-v1:1"}
                                        </code>
                                    </Typography>
                                </Alert>
                                {!isVideo && typeConfig?.supportsReferenceImage !== false ? (
                                    <>
                                        <Box>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    size="small"
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    Add reference image
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/png,image/jpeg,image/webp"
                                                        onChange={handleReferenceImageChange}
                                                    />
                                                </Button>
                                                {rawPreviewUrl ? (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        disabled={loadingReferenceFromUrl}
                                                        onClick={handleUseCurrentImageAsReference}
                                                        sx={{ textTransform: "none" }}
                                                    >
                                                        {loadingReferenceFromUrl ? (
                                                            <>
                                                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                                                Loading…
                                                            </>
                                                        ) : (
                                                            "Use current image"
                                                        )}
                                                    </Button>
                                                ) : null}
                                            </Stack>
                                            {referenceImagePreview ? (
                                                <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mt: 1.5 }}>
                                                    <Box
                                                        component="img"
                                                        src={referenceImagePreview}
                                                        alt="Reference"
                                                        sx={{
                                                            width: 120,
                                                            height: 120,
                                                            objectFit: "contain",
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            borderRadius: 1,
                                                            bgcolor: "action.hover",
                                                        }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        color="inherit"
                                                        onClick={handleClearReferenceImage}
                                                        sx={{ textTransform: "none" }}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Stack>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                                    Optional PNG, JPEG, or WebP for Bedrock image-to-image on re-generate.
                                                </Typography>
                                            )}
                                        </Box>
                                        {referenceImageBase64 ? (
                                            <TextField
                                                label="Strength"
                                                type="number"
                                                value={strength}
                                                onChange={(e) => setStrength(e.target.value)}
                                                helperText="0 = close to reference, 1 = more prompt influence. Default 0.35."
                                                fullWidth
                                                size="small"
                                                slotProps={{
                                                    htmlInput: { min: 0, max: 1, step: 0.05 },
                                                }}
                                            />
                                        ) : null}
                                    </>
                                ) : null}
                                {canRegenerate ? (
                                    <Box>
                                        <Button
                                            variant="contained"
                                            startIcon={
                                                regenerating ? (
                                                    <CircularProgress size={18} color="inherit" />
                                                ) : (
                                                    <AutorenewIcon />
                                                )
                                            }
                                            onClick={handleRegenerate}
                                            disabled={regenerating || !canSubmitRegenerate}
                                            sx={{ textTransform: "none" }}
                                        >
                                            {regenerating ? "Generating…" : "Re-Generate"}
                                        </Button>
                                    </Box>
                                ) : null}
                            </Stack>
                        </Grid>
                    </Grid>
                </Item>
            ) : null}

            <Dialog
                open={openUrlDialog}
                onClose={() => setOpenUrlDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{isVideo ? "Video URL" : "Image URL"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 0.5 }}>
                        <TextField
                            label={isVideo ? "Video URL" : "Image URL"}
                            value={(isVideo ? rawVideoUrl : rawPreviewUrl) ?? ""}
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                            slotProps={{ input: { readOnly: true } }}
                        />
                        {isVideo && rawPreviewUrl ? (
                            <TextField
                                label="Preview image URL"
                                value={rawPreviewUrl}
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                                slotProps={{ input: { readOnly: true } }}
                            />
                        ) : null}
                        {s3Key ? (
                            <TextField
                                label="S3 key"
                                value={s3Key}
                                fullWidth
                                size="small"
                                slotProps={{ input: { readOnly: true } }}
                            />
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOpenUrlDialog(false)} sx={{ textTransform: "none" }}>
                        Close
                    </Button>
                    {(isVideo ? rawVideoUrl : rawPreviewUrl) ? (
                        <Button
                            component="a"
                            href={isVideo ? rawVideoUrl : rawPreviewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                            sx={{ textTransform: "none" }}
                        >
                            {isVideo ? "Open video in new tab" : "Open in new tab"}
                        </Button>
                    ) : null}
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                title="Delete ad resource?"
                message="Removes the ad_resource row only. The S3 object is not deleted automatically. You cannot delete the last resource of a type."
                targetName={resource?.resourceType}
                targetId={id}
                callback={handleConfirmDelete}
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(v) => setSnackbar((s) => ({ ...s, open: v }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}
